/**
 * Mock API service layer.
 *
 * Every component/page talks to THESE functions — never to the raw data modules. Each
 * returns a Promise and simulates network latency (and optional random failure) so the
 * UI can exercise real loading / empty / error states.
 *
 * To integrate a real backend later: replace each function body with an axios call that
 * hits the matching endpoint (documented in docs/FLIGHT_BOOKING_MODULE_PLAN.md). The
 * request/response shapes here are the contract — keep them identical.
 *
 * @module mock/api
 */

import axios from "axios";
import countries from "./data/countries";
import cities from "./data/cities";
import airports from "./data/airports";
import airlines from "./data/airlines";
import { generateFareCalendar } from "./data/fareCalendar";
import { createBookingRecord, getBookingRecord } from "./data/bookings";

const FLIGHT_CURRENCY = "PKR";

/** Global config for the mock layer. Tweak while developing to test edge states. */
export const mockConfig = {
  minLatency: 250,
  maxLatency: 700,
  /** Probability (0-1) that a request randomly fails, to exercise error states. */
  failureRate: 0,
};

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * Resolve `value` after simulated latency, or reject to simulate a network error.
 * @template T
 * @param {T} value
 * @param {{failable?: boolean}} [opts]
 * @returns {Promise<T>}
 */
const respond = (value, { failable = true } = {}) =>
  new Promise((resolve, reject) => {
    const delay = randInt(mockConfig.minLatency, mockConfig.maxLatency);
    setTimeout(() => {
      if (failable && mockConfig.failureRate > 0 && Math.random() < mockConfig.failureRate) {
        reject(new Error("Network error: the mock service is temporarily unavailable."));
      } else {
        resolve(value);
      }
    }, delay);
  });

/* ------------------------------------------------------------------ */
/* Reference data                                                     */
/* ------------------------------------------------------------------ */

/** GET /api/countries @returns {Promise<import('./types').Country[]>} */
export const getCountries = () => respond(countries);

/** GET /api/cities @returns {Promise<import('./types').City[]>} */
export const getCities = () => respond(cities);

/**
 * GET /api/airports — optionally filtered by a query for autocomplete.
 * @param {string} [query] Matches airport name, code, city, or country.
 * @returns {Promise<import('./types').Airport[]>}
 */
export const getAirports = (query = "") => {
  const q = query.trim().toLowerCase();
  const result = !q
    ? airports
    : airports.filter((a) =>
        [a.airportName, a.airportCode, a.city, a.country]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
  return respond(result);
};

/** GET /api/airlines @returns {Promise<import('./types').Airline[]>} */
export const getAirlines = () => respond(airlines);

/* ------------------------------------------------------------------ */
/* Flights                                                            */
/* ------------------------------------------------------------------ */

/**
 * GET /api/flights/search
 * @param {import('./types').FlightSearchPayload} payload
 * @returns {Promise<import('./types').Flight[]>}
 */
export const searchFlights = async (payload = {}) => {
  const response = await axios.get("/api/flights/search", {
    params: {
      departureId: payload.from,
      arrivalId: payload.to,
      outboundDate: payload.departureDate,
      returnDate: payload.tripType === "return" ? payload.returnDate : undefined,
      currency: FLIGHT_CURRENCY,
      type: payload.tripType === "return" ? 1 : 2,
      stops: payload.directOnly ? 1 : undefined,
    },
  });

  return mapFlightResponse(response.data, payload);
};

const formatDuration = (minutes) => {
  const value = Number(minutes) || 0;
  return `${Math.floor(value / 60)}h ${value % 60}m`;
};

const formatTime = (value) => {
  const match = String(value || "").match(/(\d{1,2}):(\d{2})/);
  if (!match) return "--";
  const hour = Number(match[1]);
  const period = hour >= 12 ? "PM" : "AM";
  return `${String(hour % 12 || 12).padStart(2, "0")}:${match[2]} ${period}`;
};

const toLocalISO = (value, fallbackDate) => {
  const normalized = String(value || "").trim().replace(" ", "T");
  if (/^\d{4}-\d{2}-\d{2}T\d{1,2}:\d{2}/.test(normalized)) return normalized;
  if (/^\d{1,2}:\d{2}/.test(normalized)) return `${fallbackDate}T${normalized}`;
  return `${fallbackDate}T00:00:00`;
};

const getDayOffset = (departureISO, arrivalISO) => {
  const departureDay = new Date(`${departureISO.slice(0, 10)}T00:00:00`);
  const arrivalDay = new Date(`${arrivalISO.slice(0, 10)}T00:00:00`);
  const difference = Math.round((arrivalDay - departureDay) / 86400000);
  return Number.isFinite(difference) ? Math.max(0, difference) : 0;
};

const toAirportRef = (airport = {}) => {
  const local = airports.find((item) => item.airportCode === airport.id);
  return {
    code: airport.id || local?.airportCode || "--",
    city: local?.city || airport.name || airport.id || "Unknown",
    name: airport.name || local?.airportName || "Unknown airport",
    country: local?.country || "",
  };
};

const airlineBadge = (airlineName) => {
  const initials = String(airlineName || "FL")
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" rx="16" fill="#2642c0"/><text x="50%" y="50%" dy=".36em" text-anchor="middle" font-family="Arial" font-size="25" font-weight="700" fill="white">${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const mapFlightOption = (option, payload, index, category) => {
  const segments = Array.isArray(option.flights) ? option.flights : [];
  const firstSegment = segments[0] || {};
  const lastSegment = segments[segments.length - 1] || firstSegment;
  const layovers = Array.isArray(option.layovers) ? option.layovers : [];
  const airlineNames = [...new Set(segments.map((segment) => segment.airline).filter(Boolean))];
  const flightNumbers = segments.map((segment) => segment.flightNumber).filter(Boolean);
  const airline = airlineNames.join(" / ") || "Airline unavailable";
  const knownAirline = airlines.find((item) => item.name === airlineNames[0]);
  const departureISO = toLocalISO(firstSegment.departureAirport?.time, payload.departureDate);
  const arrivalISO = toLocalISO(lastSegment.arrivalAirport?.time, payload.departureDate);
  const durationMinutes = Number(option.totalDurationMinutes) || 0;
  const price = Number(option.price) || 0;

  return {
    id: `serp-${category}-${index}-${flightNumbers.join("-") || "flight"}`,
    airline,
    airlineCode: flightNumbers[0]?.split(/\s|-/)[0] || "",
    airlineLogo: knownAirline?.logo || airlineBadge(airline),
    flightNumber: flightNumbers.join(" / ") || "Flight number unavailable",
    departureAirport: toAirportRef(firstSegment.departureAirport),
    arrivalAirport: toAirportRef(lastSegment.arrivalAirport),
    departureTime: formatTime(firstSegment.departureAirport?.time),
    arrivalTime: formatTime(lastSegment.arrivalAirport?.time),
    departureISO,
    arrivalISO,
    arrivalDayOffset: getDayOffset(departureISO, arrivalISO),
    duration: formatDuration(durationMinutes),
    durationMinutes,
    stopCount: Math.max(layovers.length, segments.length - 1, 0),
    stopCities: layovers.map((layover) => layover.airportName || layover.airportId).filter(Boolean),
    baggageAllowance: "Not specified",
    cabinClass: payload.cabin || "Economy",
    refundable: false,
    availableSeats: 99,
    originalPrice: price,
    discount: 0,
    finalPrice: price,
    currency: FLIGHT_CURRENCY,
    aircraft: "Details unavailable",
    bookingClass: "",
  };
};

const mapFlightResponse = (data = {}, payload = {}) => {
  const best = Array.isArray(data.bestFlights) ? data.bestFlights : [];
  const other = Array.isArray(data.otherFlights) ? data.otherFlights : [];
  return [
    ...best.map((option, index) => mapFlightOption(option, payload, index, "best")),
    ...other.map((option, index) => mapFlightOption(option, payload, index, "other")),
  ];
};

/**
 * GET /api/flights/fare-calendar
 * @param {{departureDate: string, basePrice?: number, currency?: string}} params
 * @returns {Promise<import('./types').FareCalendarDay[]>}
 */
export const getFareCalendar = (params = {}) =>
  respond(generateFareCalendar(params));

/* ------------------------------------------------------------------ */
/* Bookings & payment                                                 */
/* ------------------------------------------------------------------ */

/**
 * POST /api/bookings
 * @param {import('./types').BookingPayload} payload
 * @returns {Promise<import('./types').Booking>}
 */
export const createBooking = (payload) => respond(createBookingRecord(payload));

/**
 * GET /api/bookings/:reference
 * @param {string} reference
 * @returns {Promise<import('./types').Booking>}
 */
export const getBooking = (reference) => {
  const booking = getBookingRecord(reference);
  if (!booking) return Promise.reject(new Error("Booking not found."));
  return respond(booking);
};

/**
 * POST /api/payments — always returns a mocked successful result.
 * @param {import('./types').PaymentPayload} payload
 * @returns {Promise<import('./types').PaymentResult>}
 */
export const processPayment = (payload = {}) => {
  const result = {
    success: true,
    transactionId: `TXN${randInt(100000000, 999999999)}`,
    method: payload.method,
    amount: payload.amount,
    currency: payload.currency || "PKR",
    paidAt: new Date().toISOString(),
  };
  // Payment shouldn't randomly fail in the happy-path demo.
  return respond(result, { failable: false });
};

const api = {
  mockConfig,
  getCountries,
  getCities,
  getAirports,
  getAirlines,
  searchFlights,
  getFareCalendar,
  createBooking,
  getBooking,
  processPayment,
};

export default api;
